import axios from 'axios';
import { router } from '@inertiajs/react';
import { useCallback } from 'react';

import { useToast } from '@/hook/useToast';
import { useInteractiveCallback } from '@/hook/useInteractiveCallback';
import { useRefetchReel } from '@/hook/useRefetchReel';
import { useReelMap } from '@/store/library/library.hook';

export function useDeleteReel() {
    const { showToast, hideToast } = useToast();

    const reelMap = useReelMap();
    const refetchReel = useRefetchReel();

    // NOTE: `router.reload()` only refreshes the page's `reels` list. The full
    //   reel cached in the library store (shown in the flyover) has to be
    //   refetched separately, or it keeps its pre-delete/restore status.
    const refetchCachedReel = useInteractiveCallback((reelId: number) => {
        const cachedReel = Object.values(reelMap).find(
            (reel) => reel.id === reelId,
        );

        if (cachedReel) {
            refetchReel(cachedReel.code);
        }
    });

    const restoreReel = useCallback(
        (reelId: number, reelName: string) => {
            axios
                .post(route('api.internal.reels.restore', { reelId }))
                .then(() => {
                    hideToast();

                    showToast({
                        autoClose: 5000,
                        variant: 'success',
                        label: `The video "${reelName}" has been restored`,
                    });

                    refetchCachedReel(reelId);
                    router.reload({ only: ['reels'] });
                })
                .catch(() => {
                    hideToast();

                    showToast({
                        autoClose: 5000,
                        variant: 'danger',
                        label: 'Failed to restore the video reel',
                    });
                });
        },
        [showToast, hideToast, refetchCachedReel],
    );

    const deleteReel = useCallback(
        (reelId: number, reelName: string) => {
            return axios
                .delete(route('api.internal.reels.destroy', { reelId }))
                .then(() => {
                    showToast({
                        autoClose: 5000,
                        variant: 'success',
                        label: `The video "${reelName}" has been deleted`,
                        action: {
                            label: 'Undo',
                            href: '#',
                            onClick: (e) => {
                                e.preventDefault();
                                restoreReel(reelId, reelName);
                            },
                        },
                    });

                    refetchCachedReel(reelId);
                    router.reload({ only: ['reels'] });
                })
                .catch(() => {
                    showToast({
                        autoClose: 5000,
                        variant: 'danger',
                        label: 'Failed to delete the video reel',
                    });
                });
        },
        [showToast, restoreReel, refetchCachedReel],
    );

    return { deleteReel, restoreReel };
}
